import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

function PlanEditor({ plan, onSaved }) {
    const [form, setForm] = useState({
        display_title: plan.display_title || "",
        display_amount_dollars: plan.display_amount != null ? (plan.display_amount / 100).toFixed(2) : "0.00",
        display_currency: plan.display_currency || "usd",
        display_interval: plan.display_interval || "month",
        subtitle: plan.subtitle || "",
        features: (plan.features || []).join("\n"),
        savings_badge: plan.savings_badge || "",
        price_id: plan.price_id || "",
        is_featured: !!plan.is_featured,
    });
    const [saving, setSaving] = useState(false);
    const [validating, setValidating] = useState(false);
    const [validation, setValidation] = useState(null);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(null);

    async function save() {
        setSaving(true); setError(null); setSaved(null);
        try {
            const dollars = Number(form.display_amount_dollars);
            if (!isFinite(dollars) || dollars < 0) {
                throw new Error("Enter a valid non-negative price in dollars (e.g. 350.00).");
            }
            const cents = Math.round(dollars * 100);
            const payload = {
                display_title: form.display_title,
                display_amount: cents,
                display_currency: form.display_currency,
                display_interval: form.display_interval,
                subtitle: form.subtitle,
                savings_badge: form.savings_badge,
                price_id: form.price_id,
                features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
            };
            const res = await api.adminUpdatePricing(plan.lookup_key, payload);
            setSaved({ ok: true, mismatch: res.mismatch_warning, dollars });
            onSaved?.();
        } catch (e) { setError(e.message); }
        finally { setSaving(false); }
    }
    async function validate() {
        setValidating(true); setValidation(null); setError(null);
        try {
            const r = await api.adminValidatePricing(plan.lookup_key);
            setValidation(r);
        } catch (e) { setError(e.message); }
        finally { setValidating(false); }
    }

    const dollars = Number(form.display_amount_dollars) || 0;

    return (
        <div className="st-surface p-6" data-testid={`pricing-editor-${plan.lookup_key}`}>
            <div className="flex items-center justify-between">
                <div>
                    <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>
                        {plan.lookup_key}
                    </div>
                    <h3 className="font-serif text-2xl mt-1" style={{ color: "var(--st-navy)" }}>
                        {form.display_title || "Plan"}
                    </h3>
                    <div className="mt-1 font-serif text-3xl" style={{ color: "var(--st-royal)" }} data-testid={`current-price-${plan.lookup_key}`}>
                        {dollars.toLocaleString(undefined, { style: "currency", currency: (form.display_currency || "usd").toUpperCase(), maximumFractionDigits: 2 })} / {form.display_interval}
                    </div>
                </div>
                <div className="text-right">
                    <button
                        type="button"
                        className="btn-outline-navy text-xs"
                        onClick={validate}
                        disabled={validating || !form.price_id}
                        data-testid={`validate-${plan.lookup_key}`}
                    >
                        {validating ? "Checking…" : "Validate against Stripe"}
                    </button>
                    {validation && (
                        <div className="mt-2 text-xs">
                            {validation.ok && validation.exists && validation.active ? (
                                <div style={{ color: "#067647" }} data-testid={`validate-result-${plan.lookup_key}`}>
                                    <CheckCircle2 size={12} className="inline" /> Stripe price {validation.currency.toUpperCase()}{" "}
                                    ${(validation.amount / 100).toFixed(2)} / {validation.interval}.
                                    {validation.matches_display ? " Matches." : (
                                        <span style={{ color: "#B54708" }}> Mismatch vs display amount.</span>
                                    )}
                                </div>
                            ) : (
                                <div style={{ color: "#B42318" }}>Invalid or inactive price. {validation.error || ""}</div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
                <div>
                    <label className="st-label">Display title</label>
                    <input className="st-input" value={form.display_title}
                           onChange={(e) => setForm({ ...form, display_title: e.target.value })}
                           data-testid={`title-${plan.lookup_key}`} />
                </div>
                <div>
                    <label className="st-label">Price (USD)</label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: "var(--st-silver-500)" }}>$</span>
                        <input type="number" min="0" step="0.01" className="st-input" style={{ paddingLeft: "1.5rem" }}
                               value={form.display_amount_dollars}
                               onChange={(e) => setForm({ ...form, display_amount_dollars: e.target.value })}
                               data-testid={`amount-${plan.lookup_key}`} />
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--st-silver-500)" }}>
                        Enter dollars, e.g. 350.00. Stored as {Math.round(dollars * 100).toLocaleString()} cents.
                    </div>
                </div>
                <div>
                    <label className="st-label">Interval</label>
                    <select className="st-input" value={form.display_interval}
                            onChange={(e) => setForm({ ...form, display_interval: e.target.value })}
                            data-testid={`interval-${plan.lookup_key}`}>
                        <option value="month">Monthly</option>
                        <option value="year">Yearly</option>
                    </select>
                </div>
                <div>
                    <label className="st-label">Savings badge</label>
                    <input className="st-input" value={form.savings_badge}
                           onChange={(e) => setForm({ ...form, savings_badge: e.target.value })}
                           placeholder="Save $1,200"
                           data-testid={`savings-${plan.lookup_key}`} />
                </div>
                <div className="md:col-span-2">
                    <label className="st-label">Subtitle</label>
                    <input className="st-input" value={form.subtitle}
                           onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                           data-testid={`subtitle-${plan.lookup_key}`} />
                </div>
                <div className="md:col-span-2">
                    <label className="st-label">Features (one per line)</label>
                    <textarea rows={4} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }}
                              value={form.features}
                              onChange={(e) => setForm({ ...form, features: e.target.value })}
                              data-testid={`features-${plan.lookup_key}`} />
                </div>
                <div className="md:col-span-2">
                    <label className="st-label">Stripe price_id</label>
                    <input className="st-input" value={form.price_id}
                           onChange={(e) => setForm({ ...form, price_id: e.target.value })}
                           placeholder="price_..."
                           data-testid={`price-id-${plan.lookup_key}`} />
                </div>
            </div>

            {saved?.mismatch && (
                <div className="mt-4 rounded-lg p-3 text-sm flex items-start gap-2"
                     style={{ background: "#FFFAEB", color: "#7A2E0E", border: "1px solid #FEDF89" }}
                     data-testid={`mismatch-warning-${plan.lookup_key}`}>
                    <AlertTriangle size={14} className="mt-0.5" />
                    Saved — but the new display amount does not match the previously cached Stripe amount. Validate against Stripe to confirm.
                </div>
            )}
            {saved?.ok && !saved.mismatch && (
                <div className="mt-4 text-sm" style={{ color: "#067647" }} data-testid={`saved-${plan.lookup_key}`}>Saved.</div>
            )}
            {error && <div className="mt-4 text-sm" style={{ color: "#B42318" }}>{error}</div>}

            <div className="mt-6">
                <button type="button" className="btn-royal text-sm" onClick={save} disabled={saving} data-testid={`save-${plan.lookup_key}`}>
                    {saving ? "Saving…" : "Save changes"}
                </button>
            </div>
        </div>
    );
}

export default function AdminPricing() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const res = await api.adminGetPricing();
            setPlans(res.plans);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    return (
        <section className="max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="admin-pricing-page">
            <div>
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Pricing</div>
                <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Enrollment plans</h1>
                <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                    Editing here updates the public <code>/api/pricing</code> and the landing page in real time.
                    Stripe checkout uses the <code>price_id</code> below — validate before making it live.
                </p>
            </div>

            {loading && <div className="text-sm">Loading…</div>}

            {!loading && ["swingtrust_monthly", "swingtrust_yearly"].map((key) => {
                const p = plans.find((x) => x.lookup_key === key) || { lookup_key: key };
                return <PlanEditor key={key} plan={p} onSaved={load} />;
            })}
        </section>
    );
}
